import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiX,
  FiUpload,
  FiLayers,
  FiPackage,
  FiChevronRight,
  FiChevronLeft,
  FiCheck,
  FiShield,
  FiFileText,
  FiClipboard
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import {
  mapCategoryToSector,
  normalizeSectorType,
  resolveAuthorizedTokenizerIdForSector,
  formatSectorLabel,
  allowedSectorEnumsFromIssuerProfile,
  isUiCategoryAllowedForTokenizer,
  isAssetClassSectorAllowedForTokenizer,
  makeAssetClassCode,
  buildAssetMetadata
} from '../../api/smartTokenizerMappers';
import { assetsApi } from '../../api/smartTokenizerApi';
import toast from 'react-hot-toast';

const MAX_BULK_ASSETS = 1000;

const BULK_STEPS = [
  { id: 1, label: 'Asset type' },
  { id: 2, label: 'Shared details' },
  { id: 3, label: 'Variation & naming' },
  { id: 4, label: 'Metadata' },
  { id: 5, label: 'Documents' },
  { id: 6, label: 'Review' }
];

const Shell = styled.div`
  padding: 1.5rem 1.75rem 1.75rem;
  max-height: min(88vh, 900px);
  overflow-y: auto;
  color: #e2e8f0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.35);
    border-radius: 3px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  p {
    margin: 0.45rem 0 0;
    font-size: 0.86rem;
    color: #94a3b8;
    line-height: 1.55;
    max-width: 50rem;
  }
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cbd5e1;
  border-radius: 10px;
  padding: 0.45rem 0.55rem;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    color: #fff;
    border-color: rgba(79, 172, 254, 0.4);
  }
`;

/** Matches VehicleTokenizationWizard / IssuerCreationWizard: label under circle, not beside it. */
const ProgressBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding: 0.75rem 0.5rem 0.25rem;
  gap: 0;
  row-gap: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 0 1 auto;
  min-width: 4.75rem;
  max-width: 7.5rem;
  padding: 0 0.35rem;

  .step-number {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.82rem;
    flex-shrink: 0;
    background: ${(props) =>
      props.$active ? '#4facfe' : props.$completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
    color: ${(props) => (props.$active || props.$completed ? '#fff' : '#a0a0a0')};
    border: 2px solid
      ${(props) =>
        props.$active ? '#4facfe' : props.$completed ? '#00ff88' : 'rgba(255, 255, 255, 0.2)'};
    box-shadow: ${(props) =>
      props.$active ? '0 0 14px rgba(79, 172, 254, 0.28)' : 'none'};
  }

  .step-label {
    color: ${(props) => (props.$active ? '#4facfe' : props.$completed ? '#00ff88' : '#a0a0a0')};
    font-weight: ${(props) => (props.$active ? '600' : '400')};
    font-size: 0.8rem;
    line-height: 1.3;
    text-align: center;
    width: 100%;
    hyphens: auto;
  }
`;

const StepConnector = styled.div`
  width: clamp(0.65rem, 2vw, 1.75rem);
  min-width: 0.5rem;
  height: 2px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: calc(2.25rem / 2 - 1px);
  background: ${(props) =>
    props.$completed
      ? 'linear-gradient(90deg, #00ff88 0%, rgba(79, 172, 254, 0.85) 100%)'
      : 'rgba(255, 255, 255, 0.15)'};
`;

const StepContent = styled(motion.div)`
  min-height: 320px;
`;

const Section = styled.section`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem 1.35rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0 0 0.85rem;
    font-size: 1rem;
    font-weight: 600;
    color: #f1f5f9;
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }
`;

const CategorySelection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CategoryOption = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid
    ${(props) =>
      props.$disabled ? 'rgba(255, 255, 255, 0.06)' : props.selected ? props.color : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.38 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  filter: ${(props) => (props.$disabled ? 'grayscale(0.65)' : 'none')};
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.$disabled &&
    `
    &::after {
      content: 'Not authorized';
      position: absolute;
      top: 0.65rem;
      right: 0.65rem;
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(255, 180, 180, 0.85);
      border: 1px solid rgba(255, 120, 120, 0.35);
      border-radius: 6px;
      padding: 0.2rem 0.45rem;
      background: rgba(80, 20, 20, 0.35);
      pointer-events: none;
    }
  `}

  &:hover {
    border-color: ${(props) => (!props.$disabled ? props.color : 'rgba(255, 255, 255, 0.06)')};
    transform: ${(props) => (!props.$disabled ? 'translateY(-2px)' : 'none')};
  }

  .header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .icon {
    font-size: 2rem;
  }

  .title {
    font-size: 1.15rem;
    font-weight: 600;
    color: #fff;
  }

  .description {
    color: #a0a0a0;
    font-size: 0.88rem;
    line-height: 1.45;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem 1.25rem;
`;

const Field = styled.div`
  label {
    display: block;
    font-size: 0.82rem;
    font-weight: 500;
    color: #cbd5e1;
    margin-bottom: 0.35rem;
  }
  input,
  select,
  textarea {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.25);
    color: #fff;
    font-size: 0.88rem;
    box-sizing: border-box;
    &:focus {
      outline: none;
      border-color: #4facfe;
    }
  }
  textarea {
    min-height: 72px;
    resize: vertical;
    font-family: inherit;
  }
  .hint {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.3rem;
    line-height: 1.4;
  }
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.25rem;
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.35rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.92rem;

  &.primary {
    background: #4facfe;
    color: #fff;
    &:hover:not(:disabled) {
      background: #3d8bfe;
      transform: translateY(-1px);
    }
    &:disabled {
      background: #2a2a3e;
      color: #a0a0a0;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

/** Aligned with AssetCreationWizard metadata / review layout */
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.35rem 1.5rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: #4facfe;
      flex-shrink: 0;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.35rem;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    font-weight: 500;
    color: #e2e8f0;
    margin-bottom: 0.45rem;
    font-size: 0.9rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.12);
    }

    &:disabled {
      opacity: 0.48;
      cursor: not-allowed;
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  select option {
    background: #1e293b;
    color: #fff;
  }

  .error {
    color: #f87171;
    font-size: 0.8rem;
    margin-top: 0.3rem;
  }
`;

const MetaInfoBanner = styled.div`
  margin-bottom: 1.1rem;
  padding: 0.95rem 1rem;
  background: rgba(79, 172, 254, 0.06);
  border: 1px solid rgba(79, 172, 254, 0.22);
  border-radius: 10px;
  color: #94a3b8;
  font-size: 0.88rem;
  line-height: 1.55;

  strong {
    color: #4facfe;
    font-weight: 600;
  }
`;

const BulkHintCallout = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-left: 3px solid rgba(79, 172, 254, 0.45);
  border-radius: 0 8px 8px 0;
  color: #94a3b8;
  font-size: 0.86rem;
  line-height: 1.55;
`;

const FileUploadArea = styled.button.attrs({ type: 'button' })`
  display: block;
  width: 100%;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
  background: rgba(255, 255, 255, 0.02);
  color: inherit;
  font: inherit;

  &:hover {
    border-color: #4facfe;
    background: rgba(79, 172, 254, 0.06);
  }

  .upload-icon {
    font-size: 2.75rem;
    color: #4facfe;
    margin-bottom: 0.75rem;
  }

  .upload-text {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.35rem;
    font-size: 0.95rem;
  }

  .upload-subtext {
    color: #94a3b8;
    font-size: 0.88rem;
    line-height: 1.45;
  }
`;

const UploadedFiles = styled.div`
  margin-top: 1rem;

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.85rem;
    background: rgba(79, 172, 254, 0.08);
    border: 1px solid rgba(79, 172, 254, 0.28);
    border-radius: 8px;
    margin-bottom: 0.5rem;

    .file-icon {
      color: #4facfe;
      flex-shrink: 0;
    }

    .file-name {
      flex: 1;
      color: #e2e8f0;
      font-size: 0.88rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-size {
      color: #64748b;
      font-size: 0.78rem;
      flex-shrink: 0;
    }
  }
`;

const ReviewHeading = styled.h3`
  margin: 0 0 1.35rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
`;

const ReviewStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ReviewStat = styled.div`
  padding: 0.85rem 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;

  .label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    margin-bottom: 0.35rem;
  }

  .value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #f1f5f9;
    line-height: 1.2;
  }

  .sub {
    font-size: 0.78rem;
    color: #94a3b8;
    margin-top: 0.25rem;
  }
`;

const ReviewSampleTable = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ReviewSampleHead = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(7rem, 28%);
  gap: 0.75rem;
  padding: 0.55rem 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
`;

const ReviewSampleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(7rem, 28%);
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  font-size: 0.86rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  .title {
    color: #e2e8f0;
    font-weight: 500;
  }

  .num {
    color: #64748b;
    font-family: ui-monospace, monospace;
    font-size: 0.82rem;
  }
`;

const ReviewAlert = styled.div`
  margin-top: 0.85rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  font-size: 0.84rem;
  line-height: 1.45;
  background: rgba(248, 113, 113, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #fecaca;
`;

const NextStepsPanel = styled.div`
  margin-top: 1.25rem;
  padding: 1.1rem 1.2rem;
  background: rgba(34, 197, 94, 0.06);
  border: 1px solid rgba(34, 197, 94, 0.22);
  border-radius: 10px;
  color: #a7f3d0;
  font-size: 0.88rem;
  line-height: 1.55;

  strong {
    color: #6ee7b7;
  }

  code {
    background: rgba(0, 0, 0, 0.25);
    padding: 0.12rem 0.35rem;
    border-radius: 4px;
    font-size: 0.8rem;
    color: #7dd3fc;
  }
`;

const ReviewDetailList = styled.dl`
  margin: 0;
  display: grid;
  gap: 0.65rem;

  dt {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
    margin: 0;
  }

  dd {
    margin: 0.15rem 0 0;
    color: #cbd5e1;
    font-size: 0.9rem;
    line-height: 1.45;
  }
`;

function applyTemplate(tpl, vars) {
  let s = String(tpl || '');
  Object.entries(vars).forEach(([k, v]) => {
    s = s.split(`{${k}}`).join(v == null || v === undefined ? '' : String(v));
  });
  return s;
}

function padSerial(n, width) {
  const w = Number(width);
  if (!w || w <= 0) return String(n);
  return String(Math.trunc(n)).padStart(w, '0');
}

function derivePadWidthFromSchemaPattern(pattern) {
  const p = String(pattern || '').trim();
  if (!p) return 0;
  // Try to extract the minimum from patterns like: ^[A-Z0-9]{6,12}$ or ^\\d{6,10}$
  const m = p.match(/\{(\d+)(?:,(\d*))?\}/);
  if (!m) return 0;
  const min = Number(m[1]);
  return Number.isFinite(min) && min > 0 ? min : 0;
}

function buildSerialValues(start, end, step, padWidth) {
  const a = Number(start);
  const b = Number(end);
  const st = Math.max(1, Number(step) || 1);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return [];
  const out = [];
  for (let v = a; v <= b; v += st) {
    out.push(padSerial(v, padWidth));
  }
  return out;
}

function parseColors(text) {
  return String(text || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function humanizeMetaFieldName(field) {
  return String(field)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function deriveCountryFromLocation(location) {
  const parts = String(location || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts[parts.length - 1] || '';
}

function guessSerialField(required = []) {
  const hit = required.find((k) => /serial/i.test(String(k)));
  return hit || required[0] || 'serialNumber';
}

function guessColorField(required = [], serialField) {
  const hit = required.find((k) => k !== serialField && /color|colour/i.test(String(k)));
  return hit || 'color';
}

function BulkSimilarAssetsModal({ onClose }) {
  const {
    categories,
    createAsset,
    loadAssets,
    createAssetBulkRegistration,
    submitAssetBulkRegistrationForLegalReview
  } = useTokenization();
  const { assetClasses, validateMetadataSchema } = useAssetClass();
  const { issuers, currentIssuer, isAdmin, isAuthorizedTokenizer } = useTokenIssuer();

  const sectorGateActive = isAuthorizedTokenizer && !isAdmin;
  const allowedSectorEnums = useMemo(() => {
    if (!sectorGateActive) return null;
    return allowedSectorEnumsFromIssuerProfile(currentIssuer);
  }, [sectorGateActive, currentIssuer]);

  const [currentStep, setCurrentStep] = useState(1);
  const [useAssetClassMode, setUseAssetClassMode] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [assetClassId, setAssetClassId] = useState('');
  const [baseName, setBaseName] = useState('');
  const [nameTemplate, setNameTemplate] = useState('{baseName} — SN {serial}');
  const [assetNumberTemplate, setAssetNumberTemplate] = useState('{prefix}-{serial}');
  const [description, setDescription] = useState('');
  const [valuationUSD, setValuationUSD] = useState('');
  const [ownerAddress, setOwnerAddress] = useState('');
  const [custodian, setCustodian] = useState('');
  const [location, setLocation] = useState('');
  const [country, setCountry] = useState('');
  const [metadata, setMetadata] = useState({});

  const [serialStart, setSerialStart] = useState('1');
  const [serialEnd, setSerialEnd] = useState('10');
  const [serialStep, setSerialStep] = useState('1');
  const [serialPad, setSerialPad] = useState('0');
  const [serialPadTouched, setSerialPadTouched] = useState(false);
  const [serialField, setSerialField] = useState('');

  const [colorsText, setColorsText] = useState('');
  const [colorField, setColorField] = useState('');

  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const bulkDocumentsInputRef = useRef(null);

  const categoryList = useMemo(() => Object.values(categories), [categories]);

  const currentAssetClass = useMemo(
    () => (assetClassId ? assetClasses.find((a) => a.id === assetClassId) : null),
    [assetClassId, assetClasses]
  );

  const currentCategory = useMemo(
    () => (categoryId ? categoryList.find((c) => c.id === categoryId) : null),
    [categoryId, categoryList]
  );

  const requiredMetaKeys = useMemo(() => {
    if (!currentAssetClass?.standardSchema?.required) return [];
    return [...currentAssetClass.standardSchema.required];
  }, [currentAssetClass]);

  const categoryMetaFields = useMemo(
    () => (currentCategory?.metadataFields ? [...currentCategory.metadataFields] : []),
    [currentCategory]
  );

  const serialFieldOptions = useMemo(() => {
    if (useAssetClassMode) {
      return requiredMetaKeys.length ? requiredMetaKeys : ['serialNumber', 'SerialNumber', 'serial'];
    }
    if (categoryMetaFields.length) {
      return [...new Set([...categoryMetaFields, 'serialNumber', 'serial'])];
    }
    return ['serialNumber', 'SerialNumber', 'serial'];
  }, [useAssetClassMode, requiredMetaKeys, categoryMetaFields]);

  const colorFieldOptions = useMemo(() => {
    const base = useAssetClassMode
      ? (requiredMetaKeys.length ? requiredMetaKeys.filter((k) => k !== serialField) : [])
      : categoryMetaFields.filter((k) => k !== serialField);
    const merged = [...new Set([...base, 'color', 'Color'])];
    return merged.length ? merged : ['color', 'Color'];
  }, [useAssetClassMode, requiredMetaKeys, categoryMetaFields, serialField]);

  React.useEffect(() => {
    if (!currentAssetClass?.id) return;
    const req = currentAssetClass.standardSchema?.required || [];
    const sf = guessSerialField(req);
    const cf = guessColorField(req, sf);
    setSerialField(sf);
    setColorField(cf);
    if (!serialPadTouched) {
      const spec = currentAssetClass.standardSchema?.specifications?.[sf];
      const derived = derivePadWidthFromSchemaPattern(spec?.pattern);
      if (derived > 0) {
        setSerialPad(String(derived));
      }
    }
    setMetadata((prev) => {
      const next = { ...prev };
      req.forEach((k) => {
        if (next[k] === undefined) next[k] = '';
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when asset class id changes
  }, [currentAssetClass?.id, serialPadTouched]);

  React.useEffect(() => {
    if (!useAssetClassMode || !currentAssetClass?.standardSchema || !serialField) return;
    if (serialPadTouched) return;
    const spec = currentAssetClass.standardSchema?.specifications?.[serialField];
    const derived = derivePadWidthFromSchemaPattern(spec?.pattern);
    if (derived > 0) {
      setSerialPad(String(derived));
    }
  }, [useAssetClassMode, currentAssetClass, serialField, serialPadTouched]);

  React.useEffect(() => {
    if (!currentCategory?.id || useAssetClassMode) return;
    setMetadata((prev) => {
      const next = { ...prev };
      categoryMetaFields.forEach((k) => {
        if (next[k] === undefined) next[k] = '';
      });
      return next;
    });
  }, [currentCategory?.id, useAssetClassMode, categoryMetaFields]);

  const effectiveSector = useMemo(() => {
    if (useAssetClassMode && assetClassId && currentAssetClass) {
      return currentAssetClass.sector || null;
    }
    if (categoryId) return mapCategoryToSector(categoryId);
    return null;
  }, [useAssetClassMode, assetClassId, categoryId, currentAssetClass]);

  const tokenizerCoversSector = useMemo(() => {
    if (!effectiveSector) return true;
    if (sectorGateActive && allowedSectorEnums && allowedSectorEnums.size > 0) {
      const sec = normalizeSectorType(effectiveSector);
      if (sec && allowedSectorEnums.has(sec)) {
        return Boolean(currentIssuer?.tokenizerId);
      }
    }
    return Boolean(resolveAuthorizedTokenizerIdForSector(effectiveSector, issuers, currentIssuer));
  }, [effectiveSector, issuers, currentIssuer, sectorGateActive, allowedSectorEnums]);

  const variationRows = useMemo(() => {
    const serials = buildSerialValues(serialStart, serialEnd, serialStep, serialPad);
    const colors = parseColors(colorsText);
    const rows = [];
    if (colors.length === 0) {
      serials.forEach((serial, i) => rows.push({ serial, color: '', n: i + 1 }));
    } else {
      serials.forEach((serial, si) => {
        colors.forEach((color, ci) => {
          rows.push({ serial, color, n: si * colors.length + ci + 1 });
        });
      });
    }
    return rows;
  }, [serialStart, serialEnd, serialStep, serialPad, colorsText]);

  const prefix = useMemo(() => makeAssetClassCode(baseName).slice(0, 8).toUpperCase() || 'ASSET', [baseName]);

  const previewSamples = useMemo(() => {
    return variationRows.slice(0, 3).map((row) => {
      const name = applyTemplate(nameTemplate, {
        baseName,
        serial: row.serial,
        color: row.color,
        n: String(row.n),
        prefix
      });
      const assetNumber = applyTemplate(assetNumberTemplate, {
        baseName,
        serial: row.serial,
        color: row.color,
        n: String(row.n),
        prefix
      });
      return { name, assetNumber };
    });
  }, [variationRows, nameTemplate, assetNumberTemplate, baseName, prefix]);

  const handleMetaChange = (key, value) => {
    setMetadata((prev) => ({ ...prev, [key]: value }));
  };

  const handleFiles = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    const newDocs = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
    event.target.value = '';
  }, []);

  const buildAssetPayload = (row) => {
    const meta = { ...metadata };
    if (serialField) meta[serialField] = row.serial;
    if (colorField && row.color) meta[colorField] = row.color;

    const name = applyTemplate(nameTemplate, {
      baseName,
      serial: row.serial,
      color: row.color,
      n: String(row.n),
      prefix
    });
    const assetNumber = applyTemplate(assetNumberTemplate, {
      baseName,
      serial: row.serial,
      color: row.color,
      n: String(row.n),
      prefix
    });

    return {
      category: useAssetClassMode ? '' : categoryId,
      assetClassId: useAssetClassMode ? assetClassId : '',
      isAssetClassMode: useAssetClassMode,
      name,
      assetNumber,
      description,
      valuationUSD,
      ownerAddress,
      custodian,
      location,
      country,
      metadata: meta,
      documents,
      assetClassInfo:
        useAssetClassMode && currentAssetClass
          ? {
              id: currentAssetClass.id,
              name: currentAssetClass.name,
              marketSymbol: currentAssetClass.marketSymbol,
              tradingUnit: currentAssetClass.tradingUnit,
              contractAddress: currentAssetClass.contractAddress
            }
          : null
    };
  };

  const validateStep = (step) => {
    setFormError('');
    switch (step) {
      case 1:
        if (useAssetClassMode) {
          if (!assetClassId) {
            setFormError('Select an asset class.');
            return false;
          }
          if (
            sectorGateActive &&
            currentAssetClass &&
            !isAssetClassSectorAllowedForTokenizer(currentAssetClass, allowedSectorEnums)
          ) {
            setFormError('This asset class is outside your authorized sectors.');
            return false;
          }
        } else {
          if (!categoryId) {
            setFormError('Select a category.');
            return false;
          }
          if (sectorGateActive && !isUiCategoryAllowedForTokenizer(categoryId, allowedSectorEnums)) {
            setFormError('This sector is not authorized for your tokenizer.');
            return false;
          }
        }
        return true;
      case 2:
        if (!baseName.trim()) {
          setFormError('Product / family name is required.');
          return false;
        }
        if (!description.trim()) {
          setFormError('Description is required.');
          return false;
        }
        if (!valuationUSD || Number(valuationUSD) <= 0) {
          setFormError('Valid valuation (USD) is required.');
          return false;
        }
        if (!ownerAddress.trim() || !custodian.trim()) {
          setFormError('Owner wallet and custodian are required.');
          return false;
        }
        return true;
      case 3: {
        if (!serialField.trim()) {
          setFormError('Choose the metadata field used for the serial number.');
          return false;
        }
        const colorList = parseColors(colorsText);
        if (colorList.length > 0 && !String(colorField || '').trim()) {
          setFormError('Select the metadata field for color when colors are listed.');
          return false;
        }
        const serials = buildSerialValues(serialStart, serialEnd, serialStep, serialPad);
        if (serials.length === 0) {
          setFormError('Invalid serial range (check start, end, and step).');
          return false;
        }
        if (variationRows.length > MAX_BULK_ASSETS) {
          setFormError(
            `This batch would create ${variationRows.length} assets. Maximum per run is ${MAX_BULK_ASSETS}.`
          );
          return false;
        }
        if (!nameTemplate.trim() || !assetNumberTemplate.trim()) {
          setFormError('Title and asset number templates are required.');
          return false;
        }
        return true;
      }
      case 4:
        if (useAssetClassMode && currentAssetClass && requiredMetaKeys.length > 0) {
          for (const key of requiredMetaKeys) {
            if (key === serialField || key === colorField) continue;
            if (!String(metadata[key] ?? '').trim()) {
              setFormError(`Fill metadata field "${key}" (serial/color fields are set per item automatically).`);
              return false;
            }
          }
        }
        if (!useAssetClassMode && currentCategory && categoryMetaFields.length > 0) {
          for (const key of categoryMetaFields) {
            if (key === serialField || key === colorField) continue;
            if (!String(metadata[key] ?? '').trim()) {
              setFormError(`Fill category field "${key}".`);
              return false;
            }
          }
        }
        return true;
      case 5:
        if (!documents.length) {
          setFormError('Upload at least one shared document.');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, BULK_STEPS.length));
  };

  const handlePrevious = () => {
    setFormError('');
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  const runCreate = async () => {
    setFormError('');
    if (!validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4) || !validateStep(5)) {
      setFormError('Complete all steps before creating.');
      return;
    }

    if (effectiveSector && !tokenizerCoversSector) {
      setFormError(
        `No approved authorized tokenizer covers ${formatSectorLabel(normalizeSectorType(effectiveSector))}.`
      );
      return;
    }

    if (useAssetClassMode && currentAssetClass) {
      const probeRows = [
        variationRows[0],
        variationRows[Math.min(variationRows.length - 1, 1)],
        variationRows[variationRows.length - 1]
      ].filter(Boolean);
      const seen = new Set();
      for (const row of probeRows) {
        const k = `${row.serial}|${row.color}`;
        if (seen.has(k)) continue;
        seen.add(k);
        const payload = buildAssetPayload(row);
        const { messages } = validateMetadataSchema(payload.metadata, currentAssetClass.standardSchema);
        if (messages.length > 0) {
          setFormError(`Metadata / schema (${payload.name}): ${messages.join('; ')}`);
          return;
        }
      }
    }

    setSubmitting(true);
    let ok = 0;
    let failed = 0;
    const failLabels = [];

    try {
      if (useAssetClassMode && currentAssetClass) {
        const sectorForApi = normalizeSectorType(currentAssetClass.sector) || 'RealEstate';
        const tokenizerId = resolveAuthorizedTokenizerIdForSector(
          effectiveSector || sectorForApi,
          issuers,
          currentIssuer
        );
        if (!tokenizerId) {
          setFormError(
            'No authorized tokenizer for this sector. Assign your account to a tokenizer that covers this sector.'
          );
          return;
        }

        const countryResolved =
          (country || '').trim() || deriveCountryFromLocation(location) || 'Unknown';

        const lines = variationRows.map((row) => {
          const p = buildAssetPayload(row);
          return {
            assetNumber: p.assetNumber,
            title: p.name,
            metadataDraftJson: JSON.stringify(buildAssetMetadata(p))
          };
        });

        let bulkRegistrationId;
        let assetIds = [];
        try {
          const created = await createAssetBulkRegistration({
            title: baseName.trim(),
            assetClassId: currentAssetClass.id,
            authorizedTokenizerId: tokenizerId,
            sector: sectorForApi,
            currentOwnerName: custodian.trim(),
            currentOwnerIdentifier: ownerAddress.trim(),
            country: countryResolved,
            lines
          });
          bulkRegistrationId = created.bulkRegistrationId ?? created.BulkRegistrationId;
          assetIds = created.assetIds ?? created.AssetIds ?? [];
        } catch (e) {
          const msg = e?.response?.data?.message || e?.message || 'Bulk registration failed';
          setFormError(msg);
          return;
        }

        const failedUploads = [];
        for (const assetId of assetIds) {
          for (const document of documents) {
            if (!document?.file) {
              failedUploads.push(document?.name || 'unknown file');
              continue;
            }
            try {
              const formData = new FormData();
              formData.append('documentType', document.type || 'SupportingDocument');
              formData.append('file', document.file);
              await assetsApi.uploadDocument(assetId, formData);
            } catch {
              failedUploads.push(document.name || 'document');
            }
          }
        }

        try {
          await submitAssetBulkRegistrationForLegalReview(bulkRegistrationId);
        } catch (reviewErr) {
          const revMsg = reviewErr?.response?.data?.message || reviewErr?.message || 'Unknown error';
          toast.error(`Assets were created, but bulk legal submit failed: ${revMsg}`, { duration: 8000 });
          if (failedUploads.length > 0) {
            toast.error(`Some document uploads failed: ${failedUploads.slice(0, 5).join(', ')}`, { duration: 8000 });
          }
          try {
            await loadAssets();
          } catch {
            /* ignore */
          }
          return;
        }

        ok = assetIds.length;
        try {
          await loadAssets();
        } catch {
          /* ignore */
        }
        toast.success(
          `Bulk registration: ${ok} asset${ok === 1 ? '' : 's'} created with one legal review queue item.${
            failedUploads.length > 0
              ? ` Some documents failed to upload — add them on each asset: ${failedUploads.slice(0, 4).join(', ')}${
                  failedUploads.length > 4 ? '…' : ''
                }`
              : ''
          }`
        );
        onClose();
      } else {
        for (const row of variationRows) {
          const payload = buildAssetPayload(row);
          try {
            await createAsset(payload, { quiet: true, skipGlobalLoading: true });
            ok += 1;
          } catch (e) {
            failed += 1;
            const msg = e?.response?.data?.message || e?.message || 'Failed';
            failLabels.push(`${payload.name}: ${msg}`);
          }
        }

        try {
          await loadAssets();
        } catch {
          /* ignore */
        }

        if (ok > 0) {
          toast.success(
            `Created ${ok} asset${ok === 1 ? '' : 's'} and submitted for legal review${failed ? ` (${failed} failed)` : ''}.`
          );
        }
        if (failed > 0) {
          toast.error(`${failed} failed. First error: ${failLabels[0] || 'unknown'}`, { duration: 6000 });
        }
        if (ok > 0) {
          onClose();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            key="s1"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <h3 style={{ color: '#fff', marginBottom: '1.25rem', fontSize: '1.15rem' }}>Choose asset type</h3>
            {sectorGateActive && (
              <p style={{ color: '#a0a0a0', fontSize: '0.88rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Only sectors assigned to your authorized tokenizer are enabled. All other categories are disabled.
                {allowedSectorEnums && allowedSectorEnums.size === 0 && (
                  <strong style={{ color: '#ffb4b4', display: 'block', marginTop: '0.5rem' }}>
                    No sectors are assigned to your account yet. Ask an administrator to approve your tokenizer and
                    assign sectors.
                  </strong>
                )}
              </p>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '0.5rem',
                  borderRadius: '8px'
                }}
              >
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: !useAssetClassMode ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.88rem'
                  }}
                  onClick={() => {
                    setUseAssetClassMode(false);
                    setAssetClassId('');
                    setCategoryId('');
                  }}
                >
                  Traditional categories
                </button>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: useAssetClassMode ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.88rem'
                  }}
                  onClick={() => {
                    setUseAssetClassMode(true);
                    setCategoryId('');
                    setAssetClassId('');
                  }}
                >
                  Asset classes ({assetClasses.length})
                </button>
              </div>
              <p style={{ color: '#a0a0a0', fontSize: '0.88rem', textAlign: 'center', lineHeight: 1.45 }}>
                {useAssetClassMode
                  ? 'Use predefined asset classes with standardized schemas for batch minting and spot trading.'
                  : 'Use traditional asset categories with flexible metadata fields.'}
              </p>
            </div>

            {useAssetClassMode ? (
              <div>
                {assetClasses.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '2.5rem 1.5rem',
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px dashed rgba(79, 172, 254, 0.3)',
                      borderRadius: '12px',
                      color: '#a0a0a0'
                    }}
                  >
                    <FiShield style={{ fontSize: '2.5rem', color: '#4facfe', marginBottom: '0.75rem' }} />
                    <h4 style={{ color: '#fff', marginBottom: '0.35rem' }}>No asset classes found</h4>
                    <p style={{ fontSize: '0.9rem' }}>Create asset classes first under Asset class manager.</p>
                  </div>
                ) : (
                  <CategorySelection>
                    {assetClasses.map((ac) => {
                      const acAllowed = isAssetClassSectorAllowedForTokenizer(ac, allowedSectorEnums);
                      return (
                        <CategoryOption
                          key={ac.id}
                          color="#4facfe"
                          $disabled={!acAllowed}
                          selected={assetClassId === ac.id}
                          role="button"
                          tabIndex={acAllowed ? 0 : -1}
                          aria-disabled={!acAllowed}
                          onClick={() => {
                            if (!acAllowed) return;
                            setAssetClassId(ac.id);
                          }}
                        >
                          <div className="header">
                            <span className="icon">🏭</span>
                            <span className="title">{ac.name}</span>
                          </div>
                          <div className="description">
                            {ac.marketSymbol} • {ac.tradingUnit}
                          </div>
                          <div
                            style={{
                              marginTop: '0.5rem',
                              fontSize: '0.78rem',
                              color: '#4facfe',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.25rem'
                            }}
                          >
                            {ac.standardSchema?.required?.slice(0, 4).map((field) => (
                              <span
                                key={field}
                                style={{
                                  background: 'rgba(79, 172, 254, 0.1)',
                                  padding: '0.2rem 0.45rem',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(79, 172, 254, 0.3)'
                                }}
                              >
                                {field}
                              </span>
                            ))}
                            {ac.standardSchema?.required?.length > 4 && (
                              <span style={{ color: '#a0a0a0' }}>+{ac.standardSchema.required.length - 4} more</span>
                            )}
                          </div>
                        </CategoryOption>
                      );
                    })}
                  </CategorySelection>
                )}
              </div>
            ) : (
              <CategorySelection>
                {categoryList.map((cat) => {
                  const catAllowed = isUiCategoryAllowedForTokenizer(cat.id, allowedSectorEnums);
                  return (
                    <CategoryOption
                      key={cat.id}
                      color={cat.color}
                      $disabled={!catAllowed}
                      selected={categoryId === cat.id}
                      role="button"
                      tabIndex={catAllowed ? 0 : -1}
                      aria-disabled={!catAllowed}
                      onClick={() => {
                        if (!catAllowed) return;
                        setCategoryId(cat.id);
                      }}
                    >
                      <div className="header">
                        <span className="icon">{cat.icon}</span>
                        <span className="title">{cat.name}</span>
                      </div>
                      <div className="description">{cat.description}</div>
                    </CategoryOption>
                  );
                })}
              </CategorySelection>
            )}
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            key="s2"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <Section style={{ marginBottom: 0, border: 'none', padding: 0, background: 'transparent' }}>
              <h3 style={{ marginTop: 0 }}>Shared details (every item)</h3>
              <Grid>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <label>Product / family name (for {'{baseName}'} in titles)</label>
                  <input
                    value={baseName}
                    onChange={(e) => setBaseName(e.target.value)}
                    placeholder="e.g. Reference 5513 Submariner"
                  />
                </Field>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Shared description for all items"
                  />
                </Field>
                <Field>
                  <label>Valuation (USD)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={valuationUSD}
                    onChange={(e) => setValuationUSD(e.target.value)}
                  />
                </Field>
                <Field>
                  <label>Owner wallet address</label>
                  <input value={ownerAddress} onChange={(e) => setOwnerAddress(e.target.value)} placeholder="0x…" />
                </Field>
                <Field>
                  <label>Custodian</label>
                  <input value={custodian} onChange={(e) => setCustodian(e.target.value)} />
                </Field>
                <Field>
                  <label>Location</label>
                  <input value={location} onChange={(e) => setLocation(e.target.value)} />
                </Field>
                <Field>
                  <label>Country</label>
                  <input value={country} onChange={(e) => setCountry(e.target.value)} />
                </Field>
              </Grid>
            </Section>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            key="s3"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <Section style={{ marginBottom: '1rem', border: 'none', padding: 0, background: 'transparent' }}>
              <h3 style={{ marginTop: 0 }}>What differs per unit</h3>
              <Grid>
                <Field>
                  <label>Serial start</label>
                  <input value={serialStart} onChange={(e) => setSerialStart(e.target.value)} />
                </Field>
                <Field>
                  <label>Serial end (inclusive)</label>
                  <input value={serialEnd} onChange={(e) => setSerialEnd(e.target.value)} />
                </Field>
                <Field>
                  <label>Step</label>
                  <input value={serialStep} onChange={(e) => setSerialStep(e.target.value)} />
                </Field>
                <Field>
                  <label>Zero-pad width (0 = none)</label>
                  <input
                    value={serialPad}
                    onChange={(e) => {
                      setSerialPadTouched(true);
                      setSerialPad(e.target.value);
                    }}
                  />
                  <span className="hint">Example: width 5 → 00042</span>
                </Field>
                <Field>
                  <label>Metadata field for serial</label>
                  <select value={serialField} onChange={(e) => setSerialField(e.target.value)}>
                    {serialFieldOptions.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <label>Optional colors (comma or newline separated)</label>
                  <textarea
                    value={colorsText}
                    onChange={(e) => setColorsText(e.target.value)}
                    placeholder="Leave empty if only serial varies. Example: Silver, Black, Gold"
                  />
                  <span className="hint">If set, each serial is combined with every color (cartesian product).</span>
                </Field>
                {parseColors(colorsText).length > 0 && (
                  <Field>
                    <label>Metadata field for color</label>
                    <select value={colorField} onChange={(e) => setColorField(e.target.value)}>
                      {colorFieldOptions.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </Grid>
            </Section>
            <Section style={{ marginBottom: 0, border: 'none', padding: 0, background: 'transparent' }}>
              <h3>Naming patterns</h3>
              <Grid>
                <Field>
                  <label>Title template</label>
                  <input value={nameTemplate} onChange={(e) => setNameTemplate(e.target.value)} />
                  <span className="hint">
                    <code>{'{baseName}'}</code> <code>{'{serial}'}</code> <code>{'{color}'}</code> <code>{'{n}'}</code>{' '}
                    <code>{'{prefix}'}</code>
                  </span>
                </Field>
                <Field>
                  <label>Asset number template</label>
                  <input value={assetNumberTemplate} onChange={(e) => setAssetNumberTemplate(e.target.value)} />
                  <span className="hint">
                    Prefix from name: <code>{prefix}</code>
                  </span>
                </Field>
              </Grid>
            </Section>
          </StepContent>
        );

      case 4:
        return (
          <StepContent
            key="s4"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <h3 style={{ color: '#fff', margin: '0 0 1.25rem', fontSize: '1.2rem', fontWeight: 600 }}>
              {useAssetClassMode
                ? `${currentAssetClass?.name || 'Asset class'} · Metadata`
                : `${currentCategory?.name || 'Category'} · Metadata`}
            </h3>

            <BulkHintCallout>
              These values apply to <strong>every</strong> unit in the batch. Per-item serial is stored in{' '}
              <strong>{serialField}</strong>
              {colorField ? (
                <>
                  {' '}
                  and color in <strong>{colorField}</strong>
                </>
              ) : null}
              —those keys are disabled here and filled automatically from your variation rules.
            </BulkHintCallout>

            {useAssetClassMode && currentAssetClass && requiredMetaKeys.length > 0 ? (
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <FiShield />
                    Asset class schema
                  </div>
                  <MetaInfoBanner>
                    <strong>Market:</strong> {currentAssetClass.marketSymbol}/USD · <strong>Unit:</strong>{' '}
                    {currentAssetClass.tradingUnit} · <strong>Type:</strong> {currentAssetClass.classType}
                    {currentAssetClass.sector ? (
                      <>
                        {' '}
                        · <strong>Sector:</strong>{' '}
                        {formatSectorLabel(normalizeSectorType(currentAssetClass.sector))}
                      </>
                    ) : null}
                  </MetaInfoBanner>
                  {currentAssetClass.standardSchema?.required?.map((field) => {
                    const spec = currentAssetClass.standardSchema?.specifications?.[field];
                    const disabled =
                      field === serialField || (Boolean(colorField) && field === colorField);
                    return (
                      <FormGroup key={field}>
                        <label>
                          {field.charAt(0).toUpperCase() + field.slice(1)} *
                          {spec?.unit && (
                            <span style={{ color: '#4facfe', fontSize: '0.8rem' }}> ({spec.unit})</span>
                          )}
                        </label>
                        {spec?.enum ? (
                          <select
                            value={metadata[field] ?? ''}
                            onChange={(e) => handleMetaChange(field, e.target.value)}
                            disabled={disabled}
                          >
                            <option value="">Select {field}</option>
                            {spec.enum.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={
                              spec?.type === 'number' || spec?.type === 'integer' ? 'number' : 'text'
                            }
                            placeholder={`Enter ${field}${spec?.unit ? ` (${spec.unit})` : ''}`}
                            value={metadata[field] ?? ''}
                            onChange={(e) => handleMetaChange(field, e.target.value)}
                            disabled={disabled}
                            min={spec?.min}
                            max={spec?.max}
                            step={
                              spec?.type === 'integer'
                                ? 1
                                : spec?.step !== undefined
                                  ? spec.step
                                  : spec?.type === 'number'
                                    ? 'any'
                                    : undefined
                            }
                          />
                        )}
                        {spec?.min !== undefined && spec?.max !== undefined && (
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            Range: {spec.min} – {spec.max}
                            {spec.unit ? ` ${spec.unit}` : ''}
                          </div>
                        )}
                        {spec?.patternDescription && (
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem', lineHeight: 1.45 }}>
                            {spec.patternDescription}
                          </div>
                        )}
                      </FormGroup>
                    );
                  })}
                </FormSection>
              </FormGrid>
            ) : !useAssetClassMode && currentCategory && categoryMetaFields.length > 0 ? (
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <span aria-hidden>{currentCategory.icon}</span>
                    Category fields
                  </div>
                  {categoryMetaFields.map((field) => {
                    const disabled =
                      field === serialField || (Boolean(colorField) && field === colorField);
                    return (
                      <FormGroup key={field}>
                        <label>{humanizeMetaFieldName(field)} *</label>
                        <input
                          type="text"
                          placeholder={`Enter ${field}`}
                          value={metadata[field] ?? ''}
                          onChange={(e) => handleMetaChange(field, e.target.value)}
                          disabled={disabled}
                        />
                      </FormGroup>
                    );
                  })}
                </FormSection>
              </FormGrid>
            ) : (
              <FormSection>
                <div className="section-title">
                  <FiLayers />
                  Metadata
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0, lineHeight: 1.55 }}>
                  No extra schema fields for this selection. Serial and naming still follow your variation and title
                  templates.
                </p>
              </FormSection>
            )}
          </StepContent>
        );

      case 5:
        return (
          <StepContent
            key="s5"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <h3 style={{ color: '#fff', margin: '0 0 1.25rem', fontSize: '1.2rem', fontWeight: 600 }}>
              Upload documentation
            </h3>
            <FormSection>
              <div className="section-title">
                <FiUpload />
                Shared documents
              </div>
              <p style={{ color: '#a0a0a0', fontSize: '0.88rem', margin: '0 0 1rem', lineHeight: 1.55 }}>
                The same files are attached to <strong>each</strong> asset in this batch (specifications, compliance
                packs, appraisals). Uploads use the API multipart flow (IPFS-backed); large files may take a moment.
              </p>
              <FileUploadArea onClick={() => bulkDocumentsInputRef.current?.click()}>
                <FiUpload className="upload-icon" />
                <div className="upload-text">Click to add files</div>
                <div className="upload-subtext">Multiple files supported · same set for every unit</div>
              </FileUploadArea>
              <input
                ref={bulkDocumentsInputRef}
                type="file"
                multiple
                onChange={handleFiles}
                style={{ display: 'none' }}
              />
              {documents.length > 0 && (
                <UploadedFiles>
                  {documents.map((d) => (
                    <div key={d.id} className="file-item">
                      <FiFileText className="file-icon" size={18} />
                      <span className="file-name">{d.name}</span>
                      <span className="file-size">
                        {d.size >= 1024 * 1024
                          ? `${(d.size / (1024 * 1024)).toFixed(1)} MB`
                          : `${(d.size / 1024).toFixed(1)} KB`}
                      </span>
                    </div>
                  ))}
                </UploadedFiles>
              )}
            </FormSection>
          </StepContent>
        );

      case 6:
        return (
          <StepContent
            key="s6"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <ReviewHeading>Review &amp; confirm batch</ReviewHeading>

            <FormGrid>
              <FormSection>
                <div className="section-title">
                  <FiPackage />
                  Batch overview
                </div>
                <ReviewStatGrid>
                  <ReviewStat>
                    <div className="label">Assets to create</div>
                    <div className="value">{variationRows.length}</div>
                    <div className="sub">Max {MAX_BULK_ASSETS} per run</div>
                  </ReviewStat>
                  <ReviewStat>
                    <div className="label">Documents</div>
                    <div className="value">{documents.length}</div>
                    <div className="sub">Shared across all units</div>
                  </ReviewStat>
                </ReviewStatGrid>
                {variationRows.length > MAX_BULK_ASSETS && (
                  <ReviewAlert>
                    This batch exceeds the limit of {MAX_BULK_ASSETS} assets. Reduce the serial range or color list
                    before creating.
                  </ReviewAlert>
                )}
              </FormSection>

              <FormSection>
                <div className="section-title">
                  <FiShield />
                  Program &amp; sector
                </div>
                <ReviewDetailList>
                  <div>
                    <dt>Program</dt>
                    <dd>
                      {useAssetClassMode
                        ? currentAssetClass?.name || '—'
                        : currentCategory?.name || '—'}
                    </dd>
                  </div>
                  {effectiveSector ? (
                    <div>
                      <dt>Sector (API)</dt>
                      <dd>{formatSectorLabel(normalizeSectorType(effectiveSector))}</dd>
                    </div>
                  ) : null}
                  {useAssetClassMode && currentAssetClass && (
                    <div>
                      <dt>Market / unit</dt>
                      <dd>
                        {currentAssetClass.marketSymbol}/USD · {currentAssetClass.tradingUnit}
                      </dd>
                    </div>
                  )}
                </ReviewDetailList>
              </FormSection>
            </FormGrid>

            <FormGrid>
              <FormSection>
                <div className="section-title">
                  <FiClipboard />
                  Shared record
                </div>
                <ReviewDetailList>
                  <div>
                    <dt>Product / family</dt>
                    <dd>{baseName?.trim() || '—'}</dd>
                  </div>
                  <div>
                    <dt>Valuation (USD)</dt>
                    <dd>
                      {valuationUSD !== '' && valuationUSD != null && !Number.isNaN(Number(valuationUSD))
                        ? Number(valuationUSD).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          })
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Country</dt>
                    <dd>{country?.trim() || '—'}</dd>
                  </div>
                  <div>
                    <dt>Owner wallet</dt>
                    <dd style={{ wordBreak: 'break-all' }}>{ownerAddress?.trim() || '—'}</dd>
                  </div>
                </ReviewDetailList>
              </FormSection>

              <FormSection>
                <div className="section-title">
                  <FiLayers />
                  Sample titles
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 0.5rem', lineHeight: 1.45 }}>
                  First {previewSamples.length} of {variationRows.length} generated titles and asset numbers.
                </p>
                {previewSamples.length > 0 ? (
                  <ReviewSampleTable>
                    <ReviewSampleHead>
                      <span>Title</span>
                      <span>Asset #</span>
                    </ReviewSampleHead>
                    {previewSamples.map((s, i) => (
                      <ReviewSampleRow key={i}>
                        <span className="title">{s.name}</span>
                        <span className="num">{s.assetNumber}</span>
                      </ReviewSampleRow>
                    ))}
                  </ReviewSampleTable>
                ) : (
                  <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>Adjust variation rules to see samples.</p>
                )}
              </FormSection>
            </FormGrid>

            <FormSection>
              <div className="section-title">
                <FiCheck />
                Next steps
              </div>
              <NextStepsPanel>
                {useAssetClassMode && currentAssetClass ? (
                  <>
                    <p style={{ margin: '0 0 0.65rem' }}>
                      Creating uses <code>POST /api/asset-bulk-registrations</code> (one batch + all member assets), then
                      shared documents are uploaded per asset, then{' '}
                      <code>POST /api/asset-bulk-registrations/…/submit-for-legal-review</code> for a{' '}
                      <strong>single</strong> legal review covering the whole batch. Follow it under{' '}
                      <strong>Legal reviews</strong> and individual rows under <strong>Assets</strong>.
                    </p>
                    <p style={{ margin: 0 }}>
                      After approval, open{' '}
                      <Link to="/bulk-assets-registry" style={{ color: '#7dd3fc', fontWeight: 600 }}>
                        Bulk assets registry
                      </Link>{' '}
                      (or Core Tokenizer → <strong>Bulk assets tokenization</strong>) and use <strong>Tokenize</strong> on
                      your batch when every member is approved. That runs the bulk offline mint wizard (ERC-721). You can
                      also use <Link to="/tokenization/offline-mint" style={{ color: '#7dd3fc' }}>Offline mint</Link> for
                      the same API path.
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: '0 0 0.65rem' }}>
                      Creating runs <code>POST /api/assets</code> once per unit, uploads your shared documents for each,
                      then submits <strong>each</strong> asset for legal review. Track progress under{' '}
                      <strong>Assets</strong> and <strong>Legal reviews</strong>.
                    </p>
                    <p style={{ margin: 0 }}>
                      After approval, mint each token from the registry or batch flow using your usual offline signing
                      process.
                    </p>
                  </>
                )}
              </NextStepsPanel>
            </FormSection>

            {effectiveSector && !tokenizerCoversSector && (
              <p style={{ color: '#fbbf24', fontSize: '0.86rem', marginTop: '1rem', lineHeight: 1.55 }}>
                No tokenizer covers <strong>{formatSectorLabel(normalizeSectorType(effectiveSector))}</strong>. Creation
                may fail until an authorized tokenizer is available for this sector.
              </p>
            )}
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <Shell>
      <Head>
        <div>
          <h2>
            <FiPackage /> Create many similar assets
          </h2>
          <p>
            Step-by-step: pick asset type, shared details, serial/color patterns and naming, metadata, documents, then
            review. Up to {MAX_BULK_ASSETS} assets per run; each is submitted for legal review.
          </p>
        </div>
        <CloseBtn type="button" onClick={onClose} aria-label="Close">
          <FiX size={22} />
        </CloseBtn>
      </Head>

      <ProgressBar>
        {BULK_STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <ProgressStep $active={currentStep === step.id} $completed={currentStep > step.id}>
              <div className="step-number">
                {currentStep > step.id ? <FiCheck size={15} strokeWidth={2.5} /> : step.id}
              </div>
              <div className="step-label">{step.label}</div>
            </ProgressStep>
            {index < BULK_STEPS.length - 1 ? (
              <StepConnector $completed={currentStep > step.id} aria-hidden />
            ) : null}
          </React.Fragment>
        ))}
      </ProgressBar>

      {formError ? (
        <p style={{ color: '#f87171', fontSize: '0.86rem', marginBottom: '0.75rem' }}>{formError}</p>
      ) : null}

      <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>

      <WizardActions>
        <ActionButton type="button" className="secondary" onClick={handlePrevious} disabled={currentStep === 1}>
          <FiChevronLeft />
          Previous
        </ActionButton>
        {currentStep < BULK_STEPS.length ? (
          <ActionButton type="button" className="primary" onClick={handleNext}>
            Next
            <FiChevronRight />
          </ActionButton>
        ) : (
          <ActionButton type="button" className="primary" onClick={runCreate} disabled={submitting}>
            {submitting ? 'Creating…' : `Create ${variationRows.length} asset(s)`}
            <FiCheck />
          </ActionButton>
        )}
      </WizardActions>
    </Shell>
  );
}

export default BulkSimilarAssetsModal;
